import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DealerInfo } from 'app/modules/inventory/shared/dealerInfo';
import { Message, MessageType, SeverityType } from 'app/notifications/message';
import { NotificationsService } from 'app/notifications/notifications.service';
import { LoginService } from 'app/pages/login/login.service';
import { MfaSetupSettings } from 'app/pages/models/mfa-setup-settings.model';
import { UtilsService } from 'app/shared/services/utils.service';
import { environment } from 'environments/environment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'xrm-mfa-setup',
  templateUrl: './mfa-setup.component.html',
  styleUrls: ['./mfa-setup.component.scss']
})
export class MfaSetupComponent implements OnInit, OnDestroy {

  // private fields
  private _mfaSetupSettingsSubject$ = new Subject();
  private _enableMfaSubject$ = new Subject();

  // public fields
  mfaSetupSettings: MfaSetupSettings;
  showSpinner = false;
  setupMfaFormGroup: FormGroup;

  constructor(private loginService: LoginService,
    private utilService: UtilsService,
    private router: Router,
    private fb: FormBuilder,
    private notificationService: NotificationsService) { }

  ngOnInit() {
    this.getMfaSetupSettings();
    this.initMfaSetupForm();
  }

  ngOnDestroy(): void {
    this._mfaSetupSettingsSubject$.next();
    this._mfaSetupSettingsSubject$.unsubscribe();

    this._enableMfaSubject$.next();
    this._enableMfaSubject$.unsubscribe();
  }

  /**
   * enable mfa
   */
  onSubmitClick(): void {
    if (this.setupMfaFormGroup.valid) {
      const securityPin = this.setupMfaFormGroup.controls.securityPin.value;

      this.loginService.enableMfa(securityPin)
        .pipe(takeUntil(this._enableMfaSubject$))
        .subscribe(response => {
          if (response) {
            if (response.success) {
              let nextUrl = this.router.routerState.snapshot.root.queryParams['next'];
              if (nextUrl) {
                // if contains domain redirect to the url with full path
                // (request came from webxrm login page)
                if (nextUrl.indexOf(environment.webAppMainDomain) > -1) {
                  const decodedNextUrl = decodeURIComponent(nextUrl);
                  const url = decodedNextUrl;
                  window.location.href = url;
                } else {
                  // else redirect to the angular page
                  // (request came from angular login page)
                  this.router.navigateByUrl(nextUrl);
                }
              } else {
                this.router.navigate([environment.defaultRouteAfterLogin]);
              }
            } else {
              this.notificationService.addNotification({
                source: 'MfaSetupComponent',
                type: MessageType.Toast,
                severity: SeverityType.Error,
                timeout: 5000,
                title: 'Two-factor authentication setup error',
                message: 'Invalid or expired security code, please enter new code'
              } as Message);
            }
          }
        })
    } else {
      this.setupMfaFormGroup.controls.securityPin.markAllAsTouched();
    }

  }

  /**
   * get mfa setup settings (qr code and manual entry key)
   */
  private getMfaSetupSettings(): void {
    this.showSpinner = true;

    this.loginService.getMfaSetupSettings()
      .pipe(takeUntil(this._mfaSetupSettingsSubject$))
      .subscribe(result => {
        if (!this.utilService.isNoU(result)) {
          this.mfaSetupSettings = result;
        }
      }).add(() => {
        this.showSpinner = false;
      });
  }

  /**
   * init mfa setup form
   */
  private initMfaSetupForm(): void {
    this.setupMfaFormGroup = this.fb.group({
      securityPin: new FormControl('', [
        Validators.required,
        Validators.minLength(6),
        Validators.maxLength(6)
      ])
    });
  }
}
